import { ENDPOINT_NAMES } from '../../../constants';
import type { ShowLogic, SurveyConfigBlock } from '../../../rtk/rtk.generated';
import type { ProjectApi } from '../../../rtk/types';
import type { AppDispatch, RootState } from '../../types';
import { findMatchingCachedQueries, OptimisticUpdater } from '../../utils';

interface DeleteShowLogicArgs {
  surveyConfigId: string;
  id: string;
}

interface DeleteShowLogicContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: unknown }>;
  getState: () => RootState;
}

/**
 * ### removeShowLogicFromCache
 *
 * @description
 * Removes a specific show logic item from a draft array by its ID.
 *
 * @example
 * removeShowLogicFromCache(draft, '123');
 *
 * @param draft - The draft array containing show logic items
 * @param showLogicId - The ID of the show logic item to remove
 */
const removeShowLogicFromCache = (draft: ShowLogic[] | undefined, showLogicId: string): void => {
  if (!draft) return;

  const index = draft.findIndex((item) => item.id === showLogicId);
  if (index !== -1) {
    draft.splice(index, 1);
  }
};

/**
 * ### clearShowLogicIdInBlocks
 *
 * @description
 * Clears the showLogicId field in a survey config block's question by setting it to null.
 *
 * @example
 * clearShowLogicIdInBlocks(draft, 'showlogic-123');
 *
 * @param draft - The draft array containing survey config blocks
 * @param showLogicId - The show logic ID to clear
 */
const clearShowLogicIdInBlocks = (
  draft: SurveyConfigBlock[] | undefined,
  showLogicId: string,
): void => {
  if (!draft) return;

  const block = draft.find((b) => b.surveyConfigQuestion?.showLogicId === showLogicId);
  if (block?.surveyConfigQuestion) {
    block.surveyConfigQuestion.showLogicId = null;
  }
};

/**
 * ### Delete Show Logic Enhancer
 *
 * @description
 * Enhancer for deleteSurveyconfigsBySurveyConfigIdShowlogicsAndId endpoint.
 * Optimistically removes the show logic from the cache before the server responds.
 * If the request fails, the deletion is rolled back.
 * @param api - The ProjectApi instance
 * @returns Endpoint enhancer configuration
 */
const deleteSurveyconfigsBySurveyConfigIdShowlogicsAndId = (api: ProjectApi) => ({
  async onQueryStarted(args: DeleteShowLogicArgs, context: DeleteShowLogicContext) {
    const { surveyConfigId, id } = args;
    const { dispatch, queryFulfilled, getState } = context;

    const updater = new OptimisticUpdater({ dispatch, api });

    // Optimistically remove the show logic from the cache
    updater.update({
      endpointName: ENDPOINT_NAMES.GET_SURVEYCONFIGS_BY_SURVEY_CONFIG_ID_SHOWLOGICS,
      args: { surveyConfigId },
      updateFn: (draft: ShowLogic[]) => removeShowLogicFromCache(draft, id),
    });

    // Optimistically clear the showLogicId from blocks cache
    // Only update if blocks are actually cached (performance optimization)
    const state = getState();
    const queries = state.projectApi?.queries;
    const matchingQueries = findMatchingCachedQueries<{
      projectId: number;
      surveyConfigId: string;
    }>({
      queries,
      endpointName:
        ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEY_CONFIGS_AND_SURVEY_CONFIG_ID_BLOCKS,
      matcher: (args) => args.surveyConfigId === surveyConfigId,
    });

    // Only update blocks if queries exist (skip if not loaded yet)
    matchingQueries.forEach((args) => {
      updater.update({
        endpointName:
          ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEY_CONFIGS_AND_SURVEY_CONFIG_ID_BLOCKS,
        args,
        updateFn: (draft: SurveyConfigBlock[]) => clearShowLogicIdInBlocks(draft, id),
      });
    });

    try {
      await queryFulfilled;
    } catch {
      // Rollback optimistic deletion on failure
      updater.rollback();
    }
  },
});

export default deleteSurveyconfigsBySurveyConfigIdShowlogicsAndId;
